import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { GATEWAY_URL } from './api.config';
import { AUTH_SESSION, AuthSessionKind, isSessionRevoked, RefreshableSession } from './auth-session';
import { AuthService } from './auth.service';
import { EndUserAuthService } from './end-user-auth.service';
import { InvitedAuthService } from './invited-auth.service';

/**
 * Auth M answers 401 for two unrelated things (see the error map in its
 * handler/shared.go): an EXPIRED ACCESS TOKEN, and a REJECTED CREDENTIAL — bad
 * password, wrong 2FA or phone code, wrong security pattern, dead refresh
 * token.
 *
 * Rotating the refresh token only fixes the first. Retrying the second
 * re-submits the credential behind the user's back — a second failed attempt
 * against the lockout counter, or a duplicate SMS. So the retry is opt-in per
 * endpoint rather than "anything not under /auth/": that blanket rule also
 * skipped Auth M's own resource routes, so a listing whose access token had
 * simply expired failed instead of refreshing.
 *
 * Excluded here are the CREDENTIAL FLOWS; everything else under /auth/ is a
 * plain resource route and refreshes like any other service. It is the
 * credential surface that is listed because it is the stable one — new work
 * adds resources, not new ways to authenticate — so this list needs no upkeep
 * as the API grows.
 *
 * Entries are PATH SEGMENTS, not endpoints, so a whole flow is covered at
 * once: `2fa` catches setup/confirm/verify/remove/resend/select, and
 * `reset-password` catches its send-phone-code and verify-phone children. A
 * new endpoint under an existing flow is therefore already excluded.
 *
 * Note that needing a session does NOT make a route safe to retry:
 * `change-password`, `2fa/setup` and `2fa/remove` are all session-protected
 * yet 401 on a wrong current password. That is why they appear here.
 */
const CREDENTIAL_SEGMENTS = new Set([
  // session lifecycle — `refresh` above all, which would otherwise recurse
  'refresh', 'logout', 'signin', 'register', 'status', 'verify',
  // password + pattern flows
  'change-password', 'forgot-password', 'reset-password', 'set-password',
  'forgot-pattern', 'reset-pattern', 'security-pattern', 'pattern',
  // second factor, and the one-time codes / action tokens
  '2fa', 'send-phone-code', 'verify-phone', 'verify-email', 'verify-creation',
  // first-run and account-recovery flows
  'onboarding', 'recover',
  // recovering a forgotten username: a wrong SMS code or password is a 401 too
  'username',
  // email/phone changes: a wrong SMS code or a dead link is a 401 as well, and a
  // retry would resend the code — spending another of its attempts
  'email-change', 'phone-change', 'contact-change',
]);

/** The gateway-relative path of a request — no origin, no base path, no query. */
function gatewayPath(url: string): string {
  const rest = url.slice(GATEWAY_URL.replace(/\/$/, '').length).split('?')[0];
  return rest.startsWith('/') ? rest : `/${rest}`;
}

/**
 * True when the request is part of an Auth M credential flow, so a 401 from it
 * means "wrong credential" and must be surfaced, never retried. Scoped to
 * /auth/ — the other services own their paths and keep the plain retry.
 */
function isCredentialFlow(path: string): boolean {
  return path.startsWith('/auth/') && path.split('/').some((seg) => CREDENTIAL_SEGMENTS.has(seg));
}

/**
 * The session a request authenticates with. Admin is the default, so every
 * existing call is unchanged; the invited and end-user services mark their own
 * requests (auth-session.ts explains why the URL can't decide).
 */
function sessionFor(kind: AuthSessionKind): RefreshableSession {
  if (kind === 'invited') return inject(InvitedAuthService);
  if (kind === 'end_user') return inject(EndUserAuthService);
  const admin = inject(AuthService);
  return {
    get tokens() {
      return admin.tokens;
    },
    refresh: () => admin.refresh(),
    // Admin behaviour as before: drop the dead session and let the guards take
    // the user to the login page on their next navigation.
    sessionExpired: () => admin.clearSession(),
    sessionRevoked: () => admin.signedInElsewhere(),
  };
}

/**
 * Attaches the access token of the request's session and, on a 401 that can
 * only mean an expired access token, rotates that session's refresh token once
 * and retries. Admin, invited and end-user sessions all follow the same rules.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Requests that already carry an Authorization header manage their own
  // token — leave them untouched.
  if (!req.url.startsWith(GATEWAY_URL) || req.headers.has('Authorization')) {
    return next(req);
  }

  const session = sessionFor(req.context.get(AUTH_SESSION));
  const tokens = session.tokens;
  const authed = tokens
    ? req.clone({ setHeaders: { Authorization: `Bearer ${tokens.access_token}` } })
    : req;

  return next(authed).pipe(
    catchError((err: HttpErrorResponse) => {
      // A sign-in from another browser or device replaced this session. That is
      // not an expired token (a refresh can't help): end the session and say why.
      if (err.status === 401 && tokens && isSessionRevoked(err)) {
        session.sessionRevoked();
        return throwError(() => err);
      }
      // Rotate only when the 401 can ONLY mean an expired access token: we
      // actually sent one (`tokens`), and this is not a credential flow. The
      // token check is the safety net for the list — a credential endpoint
      // reached before login has no token to expire, so it can never qualify.
      const isRetryable = !!tokens && !isCredentialFlow(gatewayPath(req.url));
      if (err.status !== 401 || !isRetryable || !session.tokens) {
        return throwError(() => err);
      }
      // Expired access token on a resource route: rotate once, then retry.
      // IMPORTANT: only end the session when the REFRESH itself fails (the
      // refresh token is dead ⇒ the session is truly over). If refresh
      // succeeds but the retried request still fails (e.g. a downstream
      // service rejects the token, or is misconfigured/down), that is THAT
      // resource's problem — propagate its error but keep the session, so one
      // flaky service can't sign the user out. Hence sessionExpired() sits on
      // refresh(), BEFORE the retry.
      return session.refresh().pipe(
        catchError((refreshErr) => {
          if (isSessionRevoked(refreshErr)) {
            session.sessionRevoked();
          } else {
            session.sessionExpired();
          }
          return throwError(() => refreshErr);
        }),
        switchMap((pair) =>
          next(req.clone({ setHeaders: { Authorization: `Bearer ${pair.access_token}` } })),
        ),
      );
    }),
  );
};
